import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { Location, DecimalPipe, DatePipe } from '@angular/common';

import { SiteService } from '../../../core/services/site.service';
import { TiltSensorService } from '../../../core/services/tilt-sensor.service';
import { LoadingService } from '../../../core/services/loading.service';
import { NotificationService } from '../../../core/services/notification.service';
import { TiltDisplacementChartComponent } from '../../../shared/charts/tilt-displacement-chart/tilt-displacement-chart.component';

@Component({
  selector: 'app-site-detail-tilt',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    TiltDisplacementChartComponent,
    DecimalPipe,
    DatePipe
  ],
  templateUrl: './site-detail-tilt.html',
  styleUrl: './site-detail-tilt.scss',
})
export class SiteDetailTiltComponent {
  private location = inject(Location);
  private siteService = inject(SiteService);
  private tiltSensorService = inject(TiltSensorService);
  private loadingService = inject(LoadingService);
  private notificationService = inject(NotificationService);

  public id = input<string | undefined>(undefined);

  public siteDetail = computed(() => {
    const siteId = this.id();
    if (siteId) {
      return this.siteService.getSiteDetail(siteId);
    }
    return undefined;
  });

  public readonly displacements = this.tiltSensorService.latestDisplacements;
  public readonly alerts = this.tiltSensorService.activeAlerts;
  public readonly hasAlerts = computed(() => this.alerts().length > 0);

  public readonly alertThreshold = computed(() => {
    const sensors = this.tiltSensorService.sensors();
    if (sensors.length > 0) {
      const threshold = Number(sensors[0].data?.['alertThreshold']);
      return !isNaN(threshold) && threshold > 0 ? threshold : 1.0;
    }
    return 1.0;
  });

  constructor() {
    let wasLoading = false;
    effect(() => {
      const isLoading = this.siteService.isLoading() || this.tiltSensorService.isLoading();
      if (isLoading && !wasLoading) {
        this.loadingService.show();
        wasLoading = true;
      } else if (!isLoading && wasLoading) {
        this.loadingService.hide();
        wasLoading = false;
      }
    });

    effect(() => {
      const siteError = this.siteService.error();
      if (siteError) {
        this.notificationService.error(siteError);
      }
    });

    effect(() => {
      const detail = this.siteDetail();
      this.tiltSensorService.siteId.set(detail?.id || null);
    });

    effect(() => {
      const sensorError = this.tiltSensorService.error();
      if (sensorError) {
        this.notificationService.error(sensorError);
      }
    });
  }

  public goBack(): void {
    this.location.back();
  }
}
